import {
  ApiPeopleAnalyticsAggregation,
  ApiPeopleAnalyticsColumn,
} from "@/types/reporting/people-analytics.types.ts";
import {
  Box,
  ButtonIcon,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  ListItem,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@skailer-com/uikit";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

// Tooltip only when the title is actually clamped (vertical overflow)
function TruncatedTitle({ title }: { title: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const id = window.requestAnimationFrame(() =>
      setIsTruncated(el.scrollHeight > el.clientHeight + 1)
    );
    return () => window.cancelAnimationFrame(id);
  }, [title]);

  return (
    <Tooltip delay={false} open={isTruncated ? undefined : false}>
      <TooltipTrigger asChild>
        <div ref={ref} className="text-title line-clamp-2">
          {title}
        </div>
      </TooltipTrigger>
      <TooltipContent className="max-w-60">{title}</TooltipContent>
    </Tooltip>
  );
}

interface MetricCardProps {
  column: ApiPeopleAnalyticsColumn;
  title?: string;
  aggregation: ApiPeopleAnalyticsAggregation;
  onChangeAggregation: (aggregation: ApiPeopleAnalyticsAggregation) => void;
  onRemove: () => void;
}

export function MetricCard({
  column,
  title = column.title,
  aggregation,
  onChangeAggregation,
  onRemove,
}: MetricCardProps) {
  const { t } = useTranslation();
  const aggregationTitle = (value: ApiPeopleAnalyticsAggregation) =>
    t(`reporting:people_analytics_aggregation_${value}`);
  const hasAggregations = column.aggregations.length > 0;

  return (
    <Box className="group flex min-h-16 items-center rounded-xs bg-light p-3 shadow-sm">
      <ListItem
        behavior="card"
        className="w-full"
        titleSlot={<TruncatedTitle title={title} />}
        caption={hasAggregations ? aggregationTitle(aggregation) : undefined}
        valueSlot={
          <DropdownMenu
            className="shrink-0 opacity-0 transition group-hover:opacity-100"
            trigger={
              <ButtonIcon
                variant="secondary"
                icon={<FontAwesomeIcon icon="fa-light fa-ellipsis" />}
              />
            }
          >
            {column.aggregations.map((value) => (
              <DropdownMenuItem
                key={value}
                title={aggregationTitle(value)}
                isSelected={value === aggregation}
                onClick={() => onChangeAggregation(value)}
              />
            ))}
            {hasAggregations && <Box className="my-1 border-t border-dark/10" />}
            <DropdownMenuItem variant="menuDanger" title={t("common:remove")} onClick={onRemove} />
          </DropdownMenu>
        }
      />
    </Box>
  );
}
